import { queryOptions, useQuery } from '@tanstack/react-query';
import { ApiError } from '../../components/ApiLoadingGuard/ApiLoadingGuard';
import { MaterialClassificationDTO, MaterialClassificationItemDTO, PredictionsService } from '../generated';
import { getConfidenceLevel } from '../../components/Prediction/utils';
import { MaterialChipData } from '../../components/Prediction/types';

export interface MaterialClassificationOptionProps {
  jobsiteId: string | undefined;
  imageId: string | undefined;
}

export function materialClassificationOptions({ jobsiteId, imageId }: MaterialClassificationOptionProps) {
  const transformFn = (resp: MaterialClassificationDTO) => transformMaterialClassification(resp?.output || []);

  return queryOptions<MaterialClassification, ApiError>({
    queryKey: ['materialClassification', jobsiteId, imageId],
    queryFn: async () => {
      if (!jobsiteId || !imageId) {
        throw new Error('jobsiteId and imageId are required');
      }

      const response = await PredictionsService.predictionControllerGetMaterialClassificationByImageId({
        path: { jobsiteId, imageId },
      });

      if (response.data !== undefined) {
        return transformFn(response.data);
      } else {
        throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!jobsiteId && !!imageId,
  });
}

export function useMaterialClassification(props: MaterialClassificationOptionProps) {
  return useQuery<MaterialClassification, ApiError>(materialClassificationOptions(props));
}

export interface MaterialPrediction {
  className: string;
  probability: number;
}

export interface MaterialClassification {
  mainChip: MaterialChipData | null;
  expandableChips: MaterialChipData[] | null;
  classId: string | null;
  rawPredictions: MaterialPrediction[];
}

export function transformMaterialClassification(
  predictionOutput: MaterialClassificationItemDTO[]
): MaterialClassification {
  if (!predictionOutput || predictionOutput.length === 0) {
    return { mainChip: null, expandableChips: null, classId: null, rawPredictions: [] };
  }

  const sortedPredictions = predictionOutput.sort((a, b) => b.probability - a.probability);
  const classId = `${sortedPredictions[0].avv}_${sortedPredictions[0].class_name}`;
  const rawPredictions: MaterialPrediction[] = sortedPredictions.map((p) => ({
    className: p.class_name,
    probability: p.probability,
  }));

  const confidenceLevel = getConfidenceLevel(sortedPredictions[0].probability);

  // If best prediction is low confidence (0.0-0.75), show "no clear assignment" + expandable chips
  if (confidenceLevel === 'low') {
    const mainChip: MaterialChipData = {
      primaryText: '',
      secondaryText: 'prediction.noClearAssignment',
      probability: 'low',
    };

    const expandableChips: MaterialChipData[] = sortedPredictions.slice(0, 2).map((x) => {
      const sText = `material.class.${x.class_name.toLowerCase()}`;
      const probability = getConfidenceLevel(x.probability);

      return {
        primaryText: x.avv,
        secondaryText: sText,
        probability: probability,
      };
    });

    return {
      mainChip,
      expandableChips,
      classId,
      rawPredictions,
    };
  }

  // For medium/high confidence, show only the main chip
  const pText = sortedPredictions[0].avv;
  const sText = `material.class.${sortedPredictions[0].class_name.toLowerCase()}`;

  const mainChip: MaterialChipData = {
    primaryText: pText,
    secondaryText: sText,
    probability: confidenceLevel,
  };

  const expandableChips: MaterialChipData[] | null = null;

  return {
    mainChip,
    expandableChips,
    classId,
    rawPredictions,
  };
}
